/**
 * V2 UnifiedGridManager ABI — aligned with takeover-app `UGM_ABI`.
 * Exported as `GRID_MANAGER_ABI` for SDK consumers.
 */
export const GRID_MANAGER_ABI = [
  {
    name: 'addBatch',
    type: 'function',
    stateMutability: 'nonpayable',
    inputs: [
      { name: 'gridId', type: 'uint256' },
      {
        name: 'params',
        type: 'tuple[]',
        components: [
          { name: 'seatId', type: 'uint256' },
          { name: 'price', type: 'uint256' },
          { name: 'maxPrice', type: 'uint256' },
          { name: 'amount', type: 'uint256' },
        ],
      },
    ],
    outputs: [],
  },
  {
    name: 'setPrice',
    type: 'function',
    stateMutability: 'nonpayable',
    inputs: [
      { name: 'gridId', type: 'uint256' },
      { name: 'seatId', type: 'uint256' },
      { name: 'newPrice', type: 'uint256' },
    ],
    outputs: [],
  },
  {
    name: 'addDeposit',
    type: 'function',
    stateMutability: 'nonpayable',
    inputs: [
      { name: 'gridId', type: 'uint256' },
      { name: 'seatId', type: 'uint256' },
      { name: 'amount', type: 'uint256' },
    ],
    outputs: [],
  },
  {
    name: 'withdrawDeposit',
    type: 'function',
    stateMutability: 'nonpayable',
    inputs: [
      { name: 'gridId', type: 'uint256' },
      { name: 'seatId', type: 'uint256' },
      { name: 'amount', type: 'uint256' },
    ],
    outputs: [],
  },
  {
    name: 'abandonSeat',
    type: 'function',
    stateMutability: 'nonpayable',
    inputs: [
      { name: 'gridId', type: 'uint256' },
      { name: 'seatId', type: 'uint256' },
    ],
    outputs: [],
  },
  {
    name: 'pendingFees',
    type: 'function',
    stateMutability: 'view',
    inputs: [
      { name: 'gridId', type: 'uint256' },
      { name: 'holder', type: 'address' },
    ],
    outputs: [{ name: '', type: 'uint256' }],
  },
  {
    name: 'claimFees',
    type: 'function',
    stateMutability: 'nonpayable',
    inputs: [{ name: 'gridIds', type: 'uint256[]' }],
    outputs: [{ name: 'total', type: 'uint256' }],
  },
  {
    name: 'pokeTax',
    type: 'function',
    stateMutability: 'nonpayable',
    inputs: [
      { name: 'gridId', type: 'uint256' },
      { name: 'seatIds', type: 'uint256[]' },
    ],
    outputs: [],
  },
  {
    name: 'holderSeatCount',
    type: 'function',
    stateMutability: 'view',
    inputs: [
      { name: 'gridId', type: 'uint256' },
      { name: 'holder', type: 'address' },
    ],
    outputs: [{ name: '', type: 'uint256' }],
  },
  {
    name: 'seats',
    type: 'function',
    stateMutability: 'view',
    inputs: [
      { name: 'gridId', type: 'uint256' },
      { name: 'seatId', type: 'uint256' },
    ],
    outputs: [
      { name: 'holder', type: 'address' },
      { name: 'price', type: 'uint96' },
      { name: 'deposit', type: 'uint96' },
      { name: 'lastTaxTime', type: 'uint32' },
      { name: 'lastPriceChangeTime', type: 'uint32' },
      { name: 'holderSince', type: 'uint32' },
      { name: 'forfeitedAt', type: 'uint32' },
      { name: 'seatEverSold', type: 'bool' },
    ],
  },
  {
    name: 'withdrawAsset',
    type: 'function',
    stateMutability: 'nonpayable',
    inputs: [
      { name: 'gridId', type: 'uint256' },
      { name: 'assetHash', type: 'bytes32' },
    ],
    outputs: [],
  },
  {
    name: 'creatorCloseGrid',
    type: 'function',
    stateMutability: 'nonpayable',
    inputs: [{ name: 'gridId', type: 'uint256' }],
    outputs: [
      { name: 'totalDeposit', type: 'uint256' },
      { name: 'totalFees', type: 'uint256' },
    ],
  },
  {
    name: 'abandonBatch',
    type: 'function',
    stateMutability: 'nonpayable',
    inputs: [
      { name: 'gridId', type: 'uint256' },
      { name: 'seatIds', type: 'uint256[]' },
    ],
    outputs: [],
  },
  {
    name: 'currentBalance',
    type: 'function',
    stateMutability: 'view',
    inputs: [
      { name: 'gridId', type: 'uint256' },
      { name: 'seatId', type: 'uint256' },
    ],
    outputs: [{ name: '', type: 'int256' }],
  },
  {
    name: 'ANTI_SNIPE_WINDOW',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ name: '', type: 'uint256' }],
  },
  {
    name: 'gridCreationPaused',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ name: '', type: 'bool' }],
  },
  {
    name: 'activeTaxToken',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ name: '', type: 'address' }],
  },
  {
    name: 'taxRevenue',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ name: 'gridId', type: 'uint256' }],
    outputs: [{ name: '', type: 'uint256' }],
  },
  {
    name: 'withdrawTaxRevenue',
    type: 'function',
    stateMutability: 'nonpayable',
    inputs: [{ name: 'gridId', type: 'uint256' }],
    outputs: [],
  },
  { type: 'error', name: 'GridDoesNotExist', inputs: [] },
  { type: 'error', name: 'SeatDoesNotExist', inputs: [] },
  { type: 'error', name: 'SeatAlreadyClaimed', inputs: [] },
  { type: 'error', name: 'SeatNotClaimed', inputs: [] },
  { type: 'error', name: 'NotSeatHolder', inputs: [] },
  { type: 'error', name: 'CannotBuyOwnSeat', inputs: [] },
  { type: 'error', name: 'PriceMustBePositive', inputs: [] },
  { type: 'error', name: 'PriceExceedsMax', inputs: [] },
  { type: 'error', name: 'InsufficientPayment', inputs: [] },
  { type: 'error', name: 'InsufficientDeposit', inputs: [] },
  { type: 'error', name: 'InsufficientBalance', inputs: [] },
  { type: 'error', name: 'CooldownActive', inputs: [] },
  { type: 'error', name: 'GridPaused', inputs: [] },
  { type: 'error', name: 'GridIsDeprecated', inputs: [] },
  { type: 'error', name: 'AntiSnipeActive', inputs: [] },
  {
    name: 'gridConfig',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ name: 'gridId', type: 'uint256' }],
    outputs: [
      { name: 'creator', type: 'address' },
      { name: 'createdAt', type: 'uint64' },
      { name: 'totalSeats', type: 'uint16' },
      { name: 'taxRateBps', type: 'uint16' },
      { name: 'taxToken', type: 'address' },
      { name: 'yieldToken', type: 'address' },
    ],
  },
  {
    name: 'GridCreationIsPaused',
    type: 'error',
    inputs: [],
  },
  {
    name: 'OnlyOneTaxTokenAllowed',
    type: 'error',
    inputs: [],
  },
] as const;

export const ERC20_APPROVE_ABI = [
  {
    name: 'approve',
    type: 'function',
    stateMutability: 'nonpayable',
    inputs: [
      { name: 'spender', type: 'address' },
      { name: 'amount', type: 'uint256' },
    ],
    outputs: [{ name: '', type: 'bool' }],
  },
] as const;
